/* eslint-disable camelcase */
import {
    TRACKING_GENERAL_BTX,
    TrackingBtx,
    TrackingCableBtx,
    TrackingFixnetProductLine,
    TrackingFixnetProductType,
    TrackingFormStatus,
    TrackingFormStepName,
    TrackingItemType,
    TrackingLoginStatus,
    TrackingOrderObject,
    TrackingOrderProduct,
    TrackingProduct,
    TrackingProductType,
    TrackingProposition,
    TrackingRetention,
    TrackingSiteArea,
    TrackingTargetAudience,
    TvConnectionType,
} from '@vfde-sails/tracking';
import { HtmlId, ProductType } from '../enum';
import { NOTIFICATION_HEADLINE_CLASSNAME } from '@vfde-brix/ws10/notification';
import { JarvisContractType, JarvisMarketability, JarvisOption, JarvisProduct, JarvisStairwayPrice, JarvisTvProduct } from '@vfde-sails/availability';

export const pageLevels = window.VF?.siteStructure
    ? [...window.VF.siteStructure]
    : [];

export const getAvcPageLevels = (step: TrackingFormStepName | string) =>
    pageLevels.concat('availability check', step);

/**
 * Get tracking site_area string for the selected product.
 * @param {ProductType} productType - selected productType
 * @returns {TrackingSiteArea} - site_area enum value
 */
export const getSiteArea = (productType: ProductType): TrackingSiteArea =>
    productType === ProductType.CABLE
        ? TrackingSiteArea.Cable
        : TrackingSiteArea.Fixnet;

/**
 * Get tracking business_transaction_type string for the selected product.
 * @param {ProductType} productType - selected productType
 * @returns {TrackingBusinessTransactionType} - business_transaction_type enum value
 */
export const getBusinessTransactionType = (productType: ProductType): TrackingBtx =>
    productType === ProductType.CABLE
        ? TrackingCableBtx.CableDigitalTv
        : TRACKING_GENERAL_BTX;

/**
 * Get tracking product_type string for the selected product.
 * @param {ProductType} type - selected productType
 * @returns {TrackingType} - product_type string value
 */
export const getProductType = (product: JarvisProduct | JarvisOption, type?: ProductType): TrackingProductType => {
    if (!type && 'trackingType' in product) {
        return product.trackingType as any as TrackingProductType;
    }

    return type === ProductType.CABLE
        ? TrackingFixnetProductType.KDG
        : TrackingFixnetProductType.CableIndependent;
};

export const getLoginStatus = (isLoggedIn: boolean): TrackingLoginStatus =>
    isLoggedIn
        ? TrackingLoginStatus.LoggedIn
        : TrackingLoginStatus.NotLoggedIn;

export const getFormStatus = (step: TrackingFormStepName): TrackingFormStatus[] => {
    switch (step) {
        case TrackingFormStepName.Show:
            return [TrackingFormStatus.Start];
        case TrackingFormStepName.Complete:
            return [ TrackingFormStatus.End, TrackingFormStatus.Success ];
        default: return [];
    }
};

export const getTvConnection = (marketability?: JarvisMarketability) =>
    marketability?.contractType === JarvisContractType.MultiCustomer
        ? TvConnectionType.MNV
        : TvConnectionType.ENV;

export const getStairwayDiscount = (stairway: JarvisStairwayPrice, price: number) =>
    (stairway.value - price).toFixed(2);

/**
 * Get tracking aPropositon object for the selected product.
 * @param {JarvisProduct} product - selected product
 * @param {ProductType} type - selected productType
 * @returns {TrackingProposition} - tracking proposition object
 */
export const getTrackingProposition = (
    product: JarvisProduct,
    type?: ProductType
): TrackingProposition => ({
    id: 1,
    name: product.id,
    target_audience: TrackingTargetAudience.Consumer,
    retention: TrackingRetention.NewContract,
    product_type: getProductType(product, type),
    product_line: TrackingFixnetProductLine.TV,
    product_category: TrackingItemType.Tariff,
    units: 1,
});

export const getTrackingProduct = (product: JarvisProduct | JarvisOption, type?: ProductType) => {
    const result: TrackingProduct = {
        sku: product.id,
        name: product.id,
        type: getProductType(product, type),
    };

    if ('duration' in product) {
        result.duration = product.duration;
    }

    if (product.price?.stairway) {
        result.price_once = product.price.stairway.value.toString();
    }

    if (typeof product.price?.value === 'number') {
        result.price_monthly = product.price.value.toString();
    }

    return result;
};

export const getTrackingOrderProduct = (
    product?: JarvisProduct | JarvisOption,
    type?: ProductType
): TrackingOrderProduct | undefined => {
    if (!product) {
        return undefined;
    }

    return {
        ...getTrackingProduct(product, type),
        proposition: 1,
        units: 1,
    };
};

export const getStairwayTrackingProduct = (product: JarvisProduct): TrackingOrderProduct | undefined => {
    if (!product.price.stairway) {
        return undefined;
    }

    return {
        sku: `${product.id}-discount`,
        name: `business action: stairway price of ${product.id}`,
        price_monthly: getStairwayDiscount(product.price.stairway, product.price.value),
        duration: product.price.stairway.period,
        type: TrackingItemType.Discount,
        proposition: 1,
        units: 1,
    };
};

export const getTrackingOrder = (product: JarvisProduct, type: ProductType, tv?: JarvisTvProduct) => {
    const products = [
        getTrackingOrderProduct(product, type),
        getStairwayTrackingProduct(product),
        getTrackingOrderProduct(tv),
        ...Object.values(product.options).map(item => getTrackingOrderProduct(item)),
        ...Object.values(product.devices).map(item => getTrackingOrderProduct(item)),
    ];

    const result: TrackingOrderObject = {
        aPropositions: [
            getTrackingProposition(product, type),
        ],
        aProducts: products.filter(item => Boolean(item)) as TrackingProduct[],
    };

    return result;
};

/**
 * Gets the innerHTML text for Notification Headline components within an Id specified element.
 * @param {HtmlId} id - element Id
 * @returns {string}
 */
export const getNotificationHeadlineText = (id: HtmlId): string =>
    document.getElementById(id)?.getElementsByClassName(NOTIFICATION_HEADLINE_CLASSNAME)[0]?.innerHTML ?? '';
